import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Truck, Store, Lock, ArrowRight, ShoppingBag, Home, TicketPercent, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { BackButton } from "@/components/BackButton";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";

const emptyAddress = { line1: "", suburb: "", city: "", province: "", postal_code: "" };

const SA_PROVINCES = [
  "Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo",
  "Mpumalanga", "Northern Cape", "North West", "Western Cape",
];

export default function CheckoutPage() {
  const { items, subtotal, clear, settings, schoolMeta } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState("delivery");
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "", notes: "",
  });
  const [address, setAddress] = useState(emptyAddress);
  const [placing, setPlacing] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // { code, label, discount }
  const [promoBusy, setPromoBusy] = useState(false);

  const promoDiscount = promo?.discount || 0;
  const subtotalAfter = Math.max(0, subtotal - promoDiscount);
  const deliveryFee = method === "delivery" ? (subtotalAfter >= (settings.free_delivery_over || 750) ? 0 : settings.delivery_fee || 85) : 0;
  const total = subtotalAfter + deliveryFee;

  // Re-price the applied code whenever the cart total changes (server re-validates
  // at order placement too — the server is always the source of truth)
  useEffect(() => {
    if (!promo) return;
    api.post("/promo/validate", { code: promo.code, subtotal })
      .then((r) => {
        if (r.data.valid) setPromo((p) => ({ ...p, discount: r.data.discount }));
        else { setPromo(null); toast.error(r.data.message); }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoInput.trim(), subtotal });
      if (data.valid) {
        setPromo({ code: data.code, label: data.label, discount: data.discount });
        toast.success(data.message);
      } else {
        toast.error(data.message || "That promo code is not valid");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPromoBusy(false);
    }
  };

  if (items.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-2xl font-bold text-navy">Your cart is empty</h1>
        <Link to="/shop" className="text-brand font-semibold text-sm mt-3 inline-block" data-testid="checkout-empty-shop">
          Back to the shop
        </Link>
      </div>
    );

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setAddr = (k) => (e) => setAddress({ ...address, [k]: e.target.value });

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name, email and phone number");
      return;
    }
    // All address fields are compulsory (delivery + invoice); only order notes are optional
    if (["line1", "suburb", "city", "province", "postal_code"].some((k) => !address[k].trim())) {
      toast.error("Please complete all address fields — street, suburb, city, province and postal code");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        customer: { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() },
        delivery_method: method,
        address,
        notes: form.notes,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty, variant: i.variant || undefined })),
        ...(promo ? { promo_code: promo.code } : {}),
        ...(schoolMeta ? { school_id: schoolMeta.school_id, school_name: schoolMeta.school_name, school_grade: schoolMeta.school_grade } : {}),
      });
      clear();
      localStorage.setItem("btm_last_order", JSON.stringify({ order_number: data.order.order_number, email: form.email.trim() }));
      toast.success(`Order ${data.order.order_number} placed`);
      navigate(`/order/${data.order.order_number}`, { state: { email: form.email.trim() } });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPlacing(false);
    }
  };

  const inputCls =
    "w-full h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="checkout-page">
      <div className="mb-4">
        <BackButton to="/cart" label="Back to cart" testId="checkout-back-button" />
      </div>
      <Reveal>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy">Checkout</h1>
        <p className="text-sm text-slate-500 mt-2 flex items-center gap-1.5">
          <Lock size={13} /> Secure checkout — no card details stored on BTM servers.
          {!user && (
            <span>
              {" "}Checking out as guest.{" "}
              <Link to="/auth" className="text-brand font-semibold" data-testid="checkout-login-link">Sign in</Link>
            </span>
          )}
        </p>
      </Reveal>

      <form onSubmit={placeOrder} className="mt-8 grid lg:grid-cols-[1fr_380px] gap-8 items-start">
        <div className="space-y-6">
          <section className="bg-white rounded-3xl border border-slate-200/80 p-6 sm:p-8" data-testid="checkout-contact">
            <h2 className="font-display font-bold text-navy text-lg mb-5">1 · Contact details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="checkout-name" />
              <input required type="tel" placeholder="Phone number" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="checkout-phone" />
              <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={`${inputCls} sm:col-span-2`} data-testid="checkout-email" />
            </div>
          </section>

          <section className="bg-white rounded-3xl border border-slate-200/80 p-6 sm:p-8" data-testid="checkout-delivery">
            <h2 className="font-display font-bold text-navy text-lg mb-5">2 · Delivery or collection</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setMethod("delivery")}
                className={`rounded-2xl border-2 p-5 text-left transition-colors ${method === "delivery" ? "border-gold bg-gold/5" : "border-slate-200 hover:border-slate-300"}`}
                data-testid="method-delivery"
              >
                <Truck size={20} className="text-brand" />
                <p className="font-display font-bold text-navy mt-2 text-sm">Courier delivery</p>
                <p className="text-xs text-slate-500 mt-1">
                  {subtotal >= (settings.free_delivery_over || 750) ? "FREE — over R750" : `${formatR(settings.delivery_fee || 85)} · free over ${formatR(settings.free_delivery_over || 750)}`}
                </p>
              </button>
              <button
                type="button"
                onClick={() => setMethod("collection")}
                className={`rounded-2xl border-2 p-5 text-left transition-colors ${method === "collection" ? "border-gold bg-gold/5" : "border-slate-200 hover:border-slate-300"}`}
                data-testid="method-collection"
              >
                <Store size={20} className="text-brand" />
                <p className="font-display font-bold text-navy mt-2 text-sm">Collect from the shop</p>
                <p className="text-xs text-slate-500 mt-1">Free · wait for order-ready confirmation</p>
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-4 mb-2">All fields are required — only order notes are optional.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Street address *" value={address.line1} onChange={setAddr("line1")} className={`${inputCls} sm:col-span-2`} data-testid="address-line1" />
              <input required placeholder="Suburb *" value={address.suburb} onChange={setAddr("suburb")} className={inputCls} data-testid="address-suburb" />
              <input required placeholder="City *" value={address.city} onChange={setAddr("city")} className={inputCls} data-testid="address-city" />
              <select
                required
                value={address.province}
                onChange={setAddr("province")}
                className={`${inputCls} ${address.province ? "text-slate-900" : "text-slate-400"}`}
                data-testid="address-province"
              >
                <option value="" disabled>Province *</option>
                {SA_PROVINCES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <input required placeholder="Postal code *" value={address.postal_code} onChange={setAddr("postal_code")} className={inputCls} data-testid="address-postal" />
            </div>
            <p className="text-[11px] text-slate-400 mt-2">{method === "delivery" ? "Your courier delivery address." : "Used as your billing address on the invoice."}</p>
            <textarea
              placeholder="Order notes (optional)"
              value={form.notes}
              onChange={set("notes")}
              rows={2}
              className="w-full mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold"
              data-testid="checkout-notes"
            />
          </section>
        </div>

        <aside className="bg-navy text-white rounded-3xl p-7 sticky top-36" data-testid="checkout-summary">
          <h3 className="font-display font-bold text-lg">Your order</h3>
          <div className="mt-4 space-y-3 max-h-56 overflow-y-auto pr-1">
            {items.map((i) => (
              <div key={i.id} className="flex justify-between gap-3 text-sm">
                <span className="text-white/70 truncate">{i.qty} × {i.name}</span>
                <span className="font-mono shrink-0">{formatR(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-white/15 space-y-2.5 text-sm">
            <div className="flex justify-between text-white/70">
              <span>Subtotal</span><span className="font-mono text-white">{formatR(subtotal)}</span>
            </div>
            {promo && (
              <div className="flex justify-between text-emerald-300" data-testid="checkout-promo-row">
                <span className="flex items-center gap-1.5">
                  Promo {promo.code}
                  <button type="button" onClick={() => setPromo(null)} className="text-white/50 hover:text-white" aria-label="Remove promo code" data-testid="checkout-promo-remove"><X size={12} /></button>
                </span>
                <span className="font-mono">-{formatR(promoDiscount)}</span>
              </div>
            )}
            <div className="flex justify-between text-white/70">
              <span>{method === "delivery" ? "Delivery" : "Collection"}</span>
              <span className="font-mono text-white" data-testid="checkout-delivery-fee">{deliveryFee === 0 ? "FREE" : formatR(deliveryFee)}</span>
            </div>
            <div className="flex justify-between font-bold text-base pt-1.5">
              <span>Total</span><span className="font-mono text-gold" data-testid="checkout-total">{formatR(total)}</span>
            </div>
          </div>

          <div className="mt-4" data-testid="checkout-promo-box">
            {promo ? (
              <p className="text-[11px] text-emerald-300 flex items-center gap-1.5"><TicketPercent size={12} /> {promo.code} — {promo.label} applied</p>
            ) : (
              <div className="flex gap-2">
                <input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="Promo code (e.g. WOND1260)"
                  className="flex-1 h-10 rounded-xl bg-white/10 border border-white/15 px-3.5 text-xs font-mono font-bold uppercase placeholder:normal-case placeholder:font-normal placeholder:text-white/40 outline-none focus:ring-2 focus:ring-gold"
                  data-testid="checkout-promo-input" />
                <button type="button" onClick={applyPromo} disabled={promoBusy || !promoInput.trim()}
                  className="h-10 px-4 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-xs font-bold disabled:opacity-40 transition-colors"
                  data-testid="checkout-promo-apply">
                  {promoBusy ? "…" : "Apply"}
                </button>
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={placing}
            className="mt-6 w-full h-12 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors gold-glow"
            data-testid="place-order-button"
          >
            {placing ? "Placing order…" : "Place order"} <ArrowRight size={15} />
          </button>
          <p className="mt-4 text-[11px] text-white/50 leading-relaxed text-center">
            Payment via secure gateway (PayFast-ready). Your order is confirmed once payment is verified.
          </p>
          <div className="mt-5 pt-4 border-t border-white/10 flex flex-col gap-2" data-testid="checkout-alt-nav">
            <button
              type="button"
              onClick={() => navigate(sessionStorage.getItem("btm_last_shop") || "/shop")}
              className="w-full h-11 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors"
              data-testid="checkout-continue-shopping"
            >
              <ShoppingBag size={15} /> Continue shopping
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="w-full h-11 rounded-full border border-white/15 text-white/70 hover:text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors"
              data-testid="checkout-home-button"
            >
              <Home size={15} /> Home page
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
